import React from 'react';
import { useContent } from '../i18n/LanguageContext';
import { CheckCircle2, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

export default function Sectors() {
  const c = useContent();
  const { sectors, problems, connectivity, hero } = c;
  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="solution" className="relative pt-8 md:pt-12 pb-20 md:pb-28 bg-white overflow-hidden">
      {/* soft accent backdrop */}
      <div className="pointer-events-none absolute -top-24 right-0 w-[38rem] h-[38rem] rounded-full bg-teal-50 blur-3xl opacity-60" />

      <div className="lt-container relative">
        <div className="max-w-3xl reveal">
          <p className="text-sm font-semibold uppercase tracking-widest text-teal-600">{sectors.eyebrow}</p>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">{sectors.title}</h2>
          <p className="mt-4 text-lg text-slate-600">{sectors.subtitle}</p>
        </div>

        {/* Sector cards */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {sectors.items.map((s, i) => (
            <div key={s.name} className="reveal card-hover group relative rounded-2xl border border-slate-200 bg-white p-6 overflow-hidden" style={{ animationDelay: `${i * 70}ms` }}>
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />
              <span className="absolute top-4 right-4 font-display text-lg font-bold text-slate-100 group-hover:text-teal-100 transition-colors">0{i + 1}</span>
              <div className="grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-600/25 group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-300">
                <s.icon className="w-7 h-7" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">{s.name}</h3>
              <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Problems */}
        <div className="mt-14 md:mt-16 grid lg:grid-cols-2 gap-8 items-stretch">
          {/* Left accent panel */}
          <div className="reveal relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-500 to-cyan-600 text-white p-8 md:p-10 flex flex-col justify-between">
            <div className="absolute inset-0 grid-dots opacity-[0.12]" />
            <div className="absolute -bottom-16 -right-10 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
            <div className="relative">
              <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/80">
                <Sparkles className="w-4 h-4" /> {problems.eyebrow}
              </p>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-bold leading-tight">{problems.title}</h2>
              <p className="mt-4 text-white/90 text-lg leading-relaxed">{problems.subtitle}</p>
            </div>
            <div className="relative mt-8 flex flex-wrap items-center gap-6">
              <div className="rounded-2xl bg-white/15 backdrop-blur px-5 py-4 border border-white/20">
                <div className="font-display text-4xl font-bold leading-none">{hero.stats[2].value}</div>
                <div className="mt-1 text-sm text-white/80">{hero.stats[2].label}</div>
              </div>
              <Button onClick={scrollToContact} className="btn-shine h-12 px-6 bg-white hover:bg-slate-50 text-teal-700 font-semibold rounded-full">
                {c.cta.demo}<ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Right: problem cards */}
          <div className="space-y-4">
            {problems.items.map((p, i) => (
              <div key={i} className="reveal group flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all duration-300 hover:border-teal-300 hover:shadow-lg hover:shadow-teal-600/10 hover:-translate-y-0.5" style={{ animationDelay: `${i * 80}ms` }}>
                <span className="mt-0.5 grid place-items-center w-10 h-10 shrink-0 rounded-xl bg-teal-50 text-teal-600 group-hover:bg-gradient-to-br group-hover:from-teal-500 group-hover:to-cyan-500 group-hover:text-white transition-colors">
                  <CheckCircle2 className="w-5 h-5" />
                </span>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">{p.q}</p>
                  <p className="text-sm text-teal-600 mt-1 inline-flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />{p.hint}
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-teal-500 group-hover:translate-x-1 transition-all mt-1" />
              </div>
            ))}
          </div>
        </div>

        {/* Connectivity band */}
        <div className="mt-14 md:mt-16 rounded-3xl bg-slate-900 text-white p-8 md:p-12 relative overflow-hidden reveal">
          <div className="absolute inset-0 grid-dots opacity-[0.08]" />
          <div className="absolute -top-20 -right-16 w-72 h-72 rounded-full bg-teal-500/10 blur-3xl" />
          <div className="relative">
            <div className="max-w-2xl">
              <h3 className="font-display text-2xl md:text-3xl font-bold">{connectivity.title}</h3>
              <p className="mt-3 text-slate-300">{connectivity.subtitle}</p>
            </div>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {connectivity.items.map((cn) => (
                <div key={cn.title} className="group rounded-2xl bg-white/5 border border-white/10 p-5 hover:bg-white/10 hover:border-teal-400/30 transition-colors">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-teal-500/15 text-teal-300 group-hover:bg-teal-500 group-hover:text-slate-900 transition-colors">
                    <cn.icon className="w-5 h-5" />
                  </span>
                  <h4 className="mt-3 font-semibold text-sm">{cn.title}</h4>
                  <p className="mt-1 text-xs text-slate-400 leading-relaxed">{cn.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
